import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { DateRange } from '@/components/DateRangeSelector'
import dayjs from 'dayjs'
import { TrendingUp, Info } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { Skeleton } from '@/components/ui/skeleton'
import { getWorkdaysReport } from '../../../services'
import { getAllDepartments } from '@/modules/Employee/services'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts'
import {
    Tooltip as UITooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'

interface MonthlyComparisonChartProps {
    dateRange: DateRange
}

// Color palette for departments
const DEPARTMENT_COLORS: Record<string, string> = {
    Marketing: '#93c5fd', // light blue
    Development: '#7dd3fc', // sky
    Technology: '#1e3a8a', // dark blue
    Finance: '#fca5a5', // light red
    CS: '#d1d5db', // gray
    Sale: '#6b7280', // dark gray
    HR: '#92400e', // brown
}

const DEFAULT_COLORS = [
    '#3b82f6',
    '#22c55e',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#ec4899',
    '#06b6d4',
    '#84cc16',
]

interface DepartmentData {
    department_id: number
    department_name: string
    late_days: number
}

const MonthlyComparisonChart: React.FC<MonthlyComparisonChartProps> = ({
    dateRange,
}) => {
    const { t } = useTranslation()

    // Calculate previous month range
    const prevDateRange = useMemo(() => {
        const currentStart = dayjs(dateRange.gte)
        const prevStart = currentStart.subtract(1, 'month').startOf('month')
        const prevEnd = currentStart.subtract(1, 'month').endOf('month')
        return {
            gte: prevStart.format('YYYY-MM-DD'),
            lte: prevEnd.format('YYYY-MM-DD'),
        }
    }, [dateRange])

    // Get month labels
    const currentMonthLabel = useMemo(() => {
        return `Tháng ${dayjs(dateRange.gte).format('M')}`
    }, [dateRange])

    const prevMonthLabel = useMemo(() => {
        return `Tháng ${dayjs(prevDateRange.gte).format('M')}`
    }, [prevDateRange])

    // Fetch current month data
    const { data: currentData, isLoading: isLoadingCurrent } =
        useQueryWithHandler(
            {
                queryKey: [
                    QUERY_KEYS.WORKDAYS_REPORT,
                    'monthly-comparison-current',
                    dateRange.gte,
                    dateRange.lte,
                ],
                queryFn: () => getWorkdaysReport(dateRange),
                staleTime: 5 * 60 * 1000,
            },
            { showLoading: false, showError: false }
        )

    // Fetch previous month data
    const { data: prevData, isLoading: isLoadingPrev } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.WORKDAYS_REPORT,
                'monthly-comparison-prev',
                prevDateRange.gte,
                prevDateRange.lte,
            ],
            queryFn: () => getWorkdaysReport(prevDateRange),
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    // Fetch all departments
    const { data: allDepartments, isLoading: isLoadingDepts } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.DEPARTMENTS, 'all'],
                queryFn: async () => {
                    const res = await getAllDepartments()
                    return res.results || []
                },
                staleTime: 10 * 60 * 1000,
            },
            { showLoading: false, showError: false }
        )

    // Process data for chart
    const { chartData, departments, departmentColorMap } = useMemo(() => {
        // Group by department
        const groupByDept = (data: any[]): DepartmentData[] => {
            const grouped = data.reduce(
                (acc, emp) => {
                    const deptId = emp.department_id
                    if (!acc[deptId]) {
                        acc[deptId] = {
                            department_id: deptId,
                            department_name: emp.department_name,
                            late_days: 0,
                        }
                    }
                    acc[deptId].late_days += emp.late_days || 0
                    return acc
                },
                {} as Record<number, DepartmentData>
            )
            return Object.values(grouped)
        }

        const prevDepts = groupByDept(prevData || [])
        const currentDepts = groupByDept(currentData || [])

        // Use all departments from API, fallback to data if API not loaded
        let deptList: { id: number; name: string }[] = []
        if (allDepartments && allDepartments.length > 0) {
            deptList = allDepartments.map((d: any) => ({
                id: d.department_id,
                name: d.department_name,
            }))
        } else {
            // Fallback: get unique departments from data
            const allDepts = new Map<number, string>()
            prevDepts.forEach((d) =>
                allDepts.set(d.department_id, d.department_name)
            )
            currentDepts.forEach((d) =>
                allDepts.set(d.department_id, d.department_name)
            )
            deptList = Array.from(allDepts.entries()).map(([id, name]) => ({
                id,
                name,
            }))
        }

        if (deptList.length === 0) {
            return { chartData: [], departments: [], departmentColorMap: {} }
        }

        // Create color map
        const colorMap: Record<string, string> = {}
        deptList.forEach((dept, index) => {
            colorMap[dept.name] =
                DEPARTMENT_COLORS[dept.name] ||
                DEFAULT_COLORS[index % DEFAULT_COLORS.length]
        })

        // Build chart data with 2 groups (prev month, current month)
        const prevMonthData: any = { month: prevMonthLabel }
        const currentMonthData: any = { month: currentMonthLabel }

        deptList.forEach((dept) => {
            const prevDept = prevDepts.find((d) => d.department_id === dept.id)
            const currentDept = currentDepts.find(
                (d) => d.department_id === dept.id
            )
            prevMonthData[dept.name] = prevDept?.late_days || 0
            currentMonthData[dept.name] = currentDept?.late_days || 0
        })

        return {
            chartData: [prevMonthData, currentMonthData],
            departments: deptList.map((d) => d.name),
            departmentColorMap: colorMap,
        }
    }, [
        currentData,
        prevData,
        allDepartments,
        prevMonthLabel,
        currentMonthLabel,
    ])

    const isLoading = isLoadingCurrent || isLoadingPrev || isLoadingDepts

    // Custom tooltip - show all departments for hovered month
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null

        return (
            <div className="bg-white p-3 border rounded-lg shadow-lg min-w-[200px]">
                <p className="font-semibold text-primary mb-2">{label}</p>
                <div className="space-y-1.5 text-sm max-h-[300px] overflow-y-auto">
                    {payload.map((entry: any, index: number) => {
                        const deptName = entry.dataKey
                        const value = entry.value || 0
                        return (
                            <div
                                key={index}
                                className="flex items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-1.5">
                                    <span
                                        className="w-2.5 h-2.5 rounded-sm"
                                        style={{ backgroundColor: entry.fill }}
                                    />
                                    <span className="text-gray-700 truncate max-w-[120px]">
                                        {deptName}
                                    </span>
                                </div>
                                <span className="font-medium text-gray-900">
                                    {value} lần
                                </span>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    // Abbreviate department name for legend
    const abbreviateName = (name: string, maxLen = 12): string => {
        if (!name) return '?'
        if (name.length <= maxLen) return name
        const words = name.trim().split(/\s+/)
        if (words.length >= 2) {
            return words.map((w) => w.charAt(0).toUpperCase()).join('')
        }
        return name.slice(0, maxLen - 2) + '..'
    }

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100 h-full">
                <Skeleton className="h-5 w-48 mb-4" />
                <Skeleton className="h-[280px] w-full" />
            </div>
        )
    }

    return (
        <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100 h-full">
            <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-primary" />
                <Typogaphy className="font-semibold text-primary">
                    {t(
                        'workday.monthly_comparison',
                        'So sánh tỉ lệ giữa 2 tháng liền kề'
                    )}
                </Typogaphy>
                <TooltipProvider>
                    <UITooltip>
                        <TooltipTrigger asChild>
                            <button className="text-gray-400 hover:text-gray-600">
                                <Info className="w-4 h-4" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-xs text-xs">
                                So sánh số lần đi trễ theo phòng ban giữa{' '}
                                {prevMonthLabel} và {currentMonthLabel}
                            </p>
                        </TooltipContent>
                    </UITooltip>
                </TooltipProvider>
            </div>

            {chartData.length === 0 || departments.length === 0 ? (
                <div className="flex items-center justify-center h-[280px] text-gray-500 text-sm">
                    {t('workday.no_data', 'Không có dữ liệu')}
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
                    >
                        <XAxis
                            dataKey="month"
                            stroke="#9ca3af"
                            fontSize={12}
                            tickLine={false}
                        />
                        <YAxis stroke="#9ca3af" fontSize={11} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{
                                fontSize: '11px',
                                paddingTop: '10px',
                            }}
                            iconSize={10}
                            formatter={(value) => abbreviateName(value, 12)}
                        />
                        {departments.map((dept) => (
                            <Bar
                                key={dept}
                                dataKey={dept}
                                fill={departmentColorMap[dept]}
                                radius={[2, 2, 0, 0]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    )
}

export default MonthlyComparisonChart
